import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../App';
import BottomNavigation from '../components/BottomNavigation';
import LanguageToggle from '../components/LanguageToggle';
import { LogOut, Calendar, MapPin, Clock, ChevronRight, Rocket, Search, X } from 'lucide-react';
import axios from 'axios';
import { formatPlaceName } from '../utils/placeName';
import AddToCalendarButton from '../components/AddToCalendarButton';

const API = process.env.REACT_APP_BACKEND_URL;

const Events = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { logout, user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  // Iter 195 — Client-side search across the already-fetched list. No backend
  // change: the events feed is small enough to filter in-memory on title /
  // description / business name / location. Mirrors the Discover search UX.
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    axios.get(`${API}/api/events`)
      .then(res => setEvents(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleClick = (event) => {
    axios.post(`${API}/api/posts/${event.id}/click`, {}).catch(() => {});
    // Open the dedicated, shareable event page (Iter — deep links).
    navigate(`/events/${event.id}`);
  };

  // Track views
  useEffect(() => {
    events.forEach(e => {
      axios.post(`${API}/api/posts/${e.id}/view`, {}).catch(() => {});
    });
  }, [events]);

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    } catch { return ''; }
  };

  const formatTime = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
  };

  // Iter 195 — Filter the loaded events on the fly. Case-insensitive contains
  // across the four fields users would naturally search by. Empty search →
  // unfiltered original list.
  const normalisedSearch = searchTerm.trim().toLowerCase();
  const visibleEvents = normalisedSearch
    ? events.filter((e) => {
        const haystack = [e.title, e.description, e.businessName, e.location, e.businessCity]
          .filter(Boolean).join(' ').toLowerCase();
        return haystack.includes(normalisedSearch);
      })
    : events;

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <header className="bg-gradient-to-r from-purple-600 to-purple-700 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold flex items-center gap-2" data-testid="events-title">
              <Calendar className="h-6 w-6" />
              {t('nav.events', 'Events')}
            </h1>
            <div className="flex items-center gap-3">
              <LanguageToggle />
              {user ? (
                <button onClick={() => { logout(); navigate('/'); }}
                  className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                  data-testid="logout-button">
                  <LogOut className="h-4 w-4" /><span className="hidden sm:inline">{t('logout', 'Logout')}</span>
                </button>
              ) : (
                <button onClick={() => navigate('/signin')}
                  className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                  data-testid="signin-button">
                  <span>{t('signIn', 'Sign In')}</span>
                </button>
              )}
            </div>
          </div>

          {/* Iter 195 — Search row. Same UX as Discover: icon inside the input,
              debounce-free instant client-side filter on type, Enter is a
              no-op (results already update on every keystroke), inline × clear. */}
          <div className="mt-3 relative">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-purple-400"
              aria-hidden="true"
            />
            <input
              type="search"
              inputMode="search"
              enterKeyHint="search"
              placeholder={t('searchEventsPlaceholder', 'Search events...')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
              className="w-full pl-10 pr-10 py-2 rounded-lg text-gray-900 focus:ring-2 focus:ring-white outline-none"
              data-testid="events-search-input"
              aria-label={t('searchEventsPlaceholder', 'Search events...')}
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                data-testid="events-search-clear-btn"
                aria-label={t('clearSearch', 'Clear search')}
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-6">
        {loading ? (
          <div className="space-y-4">
            {[1,2,3].map(i => <div key={i} className="bg-white rounded-xl h-28 animate-pulse" />)}
          </div>
        ) : events.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-8 text-center">
            <Calendar className="h-14 w-14 text-gray-300 mx-auto mb-3" />
            <h2 className="text-lg font-semibold text-gray-900 mb-1">{t('noEventsYet', 'No Events Yet')}</h2>
            <p className="text-sm text-gray-500 mb-5">{t('eventsWillAppear', 'Events from local businesses will appear here soon!')}</p>
            <button onClick={() => navigate('/discover')}
              className="px-5 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium">
              {t('browseBusinesses', 'Browse Businesses')}
            </button>
          </div>
        ) : visibleEvents.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-8 text-center" data-testid="events-no-search-results">
            <Calendar className="h-14 w-14 text-gray-300 mx-auto mb-3" />
            <h2 className="text-lg font-semibold text-gray-900 mb-1">{t('noEventsForSearch', 'No events match your search')}</h2>
            <p className="text-sm text-gray-500 mb-5">&ldquo;{searchTerm}&rdquo;</p>
            <button onClick={() => setSearchTerm('')}
              className="px-5 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
              data-testid="events-clear-search-cta">
              {t('clearSearch', 'Clear search')}
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-gray-500">{visibleEvents.length} {t('upcomingEvents', 'upcoming events')}</p>
            {visibleEvents.map((event) => (
              <div key={event.id} onClick={() => handleClick(event)}
                role="button" tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') handleClick(event); }}
                className="w-full text-left bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow group cursor-pointer"
                data-testid={`event-card-${event.id}`}>
                <div className="p-4 flex items-start gap-3">
                  {/* Date badge (always shown — anchors the card by date) */}
                  <div className="w-14 h-14 bg-purple-100 rounded-xl flex flex-col items-center justify-center flex-shrink-0">
                    <span className="text-[10px] text-purple-500 font-medium uppercase">{formatDate(event.startDate).split(' ')[0]}</span>
                    <span className="text-lg font-bold text-purple-600 leading-none">{new Date(event.startDate).getDate()}</span>
                  </div>
                  {/* Event image — only when uploaded; sits between date and text */}
                  {event.image && (
                    <img
                      src={event.image}
                      alt={event.title}
                      loading="lazy"
                      className="w-14 h-14 rounded-xl object-cover flex-shrink-0 border border-purple-100"
                      data-testid={`event-card-image-${event.id}`}
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-gray-900 truncate text-sm group-hover:text-purple-600 transition-colors">{event.title}</h3>
                      {event.boosted && (
                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-gradient-to-r from-amber-400 to-orange-400 text-white text-[10px] font-bold rounded-full flex-shrink-0" data-testid={`sponsored-badge-${event.id}`}>
                          <Rocket className="h-2.5 w-2.5" />{t('sponsored', 'Sponsored')}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{event.description}</p>
                    <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                      {event.businessName && (
                        <span className="font-medium text-gray-600">{event.businessName}</span>
                      )}
                      {(event.location || event.businessCity) && (
                        <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{formatPlaceName(event.location || event.businessCity)}</span>
                      )}
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />{formatTime(event.startDate)}
                      </span>
                      {event.price != null && event.price > 0 && (
                        <span className="text-purple-600 font-medium">{event.price}€</span>
                      )}
                    </div>
                    <div className="mt-2" onClick={(e) => e.stopPropagation()}>
                      <AddToCalendarButton event={event} business={{ businessName: event.businessName, city: event.businessCity }} variant="small" />
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-300 group-hover:text-purple-400 transition-colors flex-shrink-0 mt-2" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <BottomNavigation />
    </div>
  );
};

export default Events;
